import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Root, classes } from './utils';
import { postOtp, resendOtpApi } from '../../../../services/accountSetting';
import AuthenticationLeftGrid from '../common/AuthenticationLeftGrid';
import logoIcon from '../../../../assets/common-assets/docu-transcribe-logo(1).png';
import { lightPalette } from '../../../../theme';
import Toast from '../../utils/Toast';
import ROLES from '../../../../constants/roles';
import { getLoggedInUser } from '../../../../store/userSlice';
import { getLoggedInUserDetail } from '../../../../services/userApi';
import {
  INVALID_INPUT_REGEX,
  OTP_SECTION,
} from '../../../../constants/staticContent';
import { calculateTotalSeconds, expiryTimeCalculation } from './couter';
import { removeLocalStorageItem } from '../../../../services/localStorageUtils';

const OtpSection = () => {
  const otpLength = 6;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // set expiry according to database
  const formattedDifference = expiryTimeCalculation();
  const secondsStr = calculateTotalSeconds(formattedDifference);

  const [otp, setOtp] = useState(new Array(otpLength).fill(''));
  const [disableFields, setDisableFields] = useState(false);
  const [expiryTime, setExpiryTime] = useState(secondsStr);
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState(false);

  const useClearLocalStorageOnTabClose = () => {
    useEffect(() => {
      const clearLocalStorage = () => {
        localStorage.clear();
      };

      window.addEventListener('beforeunload', clearLocalStorage);

      return () => {
        window.removeEventListener('beforeunload', clearLocalStorage);
      };
    }, []);
  };
  useClearLocalStorageOnTabClose();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setExpiryTime((prevExpiryTime) => {
        if (prevExpiryTime > 0) {
          return prevExpiryTime - 1;
        }
        setDisableFields(true);
        clearInterval(intervalId);
        return 0;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [expiryTime]);

  useEffect(() => {
    if (expiryTime === 0) {
      setOtp(new Array(otpLength).fill(''));
      removeLocalStorageItem('OtpExp');
      setDisableFields(true);
    }
  }, [expiryTime]);

  const handleChange = (index, value) => {
    if (INVALID_INPUT_REGEX.OTP_DIGIT.test(value) && value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      const allFieldsFilled = newOtp.every((val) =>
        INVALID_INPUT_REGEX.ONLY_OTP_DIGIT.test(val),
      );
      setOtpError(allFieldsFilled ? '' : `${otpError}`);
      if (value && index < 5) {
        document.getElementById(`otpInput${index + 2}`).focus();
      }
    }
  };

  const handleFocus = async (index) => {
    if (disableFields) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      setDisableFields(false);
    }
  };

  const handleClick = (index) => {
    const inputElement = document.getElementById(`otpInput${index + 1}`);
    if (inputElement) {
      const { length } = inputElement.value;
      inputElement.setSelectionRange(length, length);
    }
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    const allFieldsFilled = otp.every((value) =>
      INVALID_INPUT_REGEX.ONLY_OTP_DIGIT.test(value),
    );
    if (allFieldsFilled) {
      setOtpError(false);
      const sendOtp = otp.join('');
      try {
        const verificationUser = await postOtp(sendOtp);
        if (
          verificationUser?.roles[0] === ROLES.MANAGER ||
          verificationUser?.roles[0] === ROLES.MEMBER ||
          verificationUser?.roles[0] === ROLES.ADMIN
        ) {
          navigate('/invoices');
        } else if (verificationUser?.roles[0] === ROLES.SALES_ADMIN) {
          navigate('/sales-admin-dashboard');
        } else {
          navigate('/dashboard');
        }
        const loggedInUser = await getLoggedInUserDetail();
        dispatch(getLoggedInUser(loggedInUser));
        Toast({ message: verificationUser?.message, variant: 'success' });
        setOtp(new Array(otpLength).fill(''));
      } catch (error) {
        return error;
      }
    } else {
      setOtpError(true);
      handleFocus(0);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (index > 0 && !newOtp[index] && newOtp[index - 1]) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        const prevInput = document.getElementById(`otpInput${index}`);
        if (prevInput) {
          prevInput.focus();
          setOtpError(true);
        }
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (index > 0) {
        const prevInput = document.getElementById(`otpInput${index}`);
        if (prevInput) {
          prevInput.focus();
          if (inputRef.current) {
            const { length } = prevInput.value;
            inputRef.current.setSelectionRange(length, length);
          }
        }
      }
    } else if (e.key === 'ArrowRight') {
      if (index < otp.length - 1) {
        const nextInput = document.getElementById(`otpInput${index + 2}`);
        if (nextInput) {
          nextInput.focus();
        }
      }
    } else if (e.key === 'Enter') {
      handleSubmitOtp(e);
    }
  };

  const handleResendOtp = async (e) => {
    e.preventDefault();
    setOtp(new Array(otpLength).fill(''));
    setIsLoading(true);
    const resendOtp = await resendOtpApi();
    if (resendOtp) {
      handleFocus(0);
      setOtpError(false);
      setIsLoading(false);
      setDisableFields(false);
      setExpiryTime(resendOtp);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];
    pasteData.split('').forEach((char, index) => {
      newOtp[index] = char;
    });
    setOtp(newOtp);

    const inputs = document.querySelectorAll('input[type="text"]');
    const lastInput = inputs[inputs.length - 1];
    lastInput.focus();
  };

  const renderResendButton = (loading) => {
    if (loading) {
      return (
        <Box>
          <CircularProgress className={classes.loaderBox} size={18} />
        </Box>
      );
    }
    return <Button className={classes.resendButton}>RESEND</Button>;
  };

  return (
    <Root>
      <Grid container component="main" className={classes.containerMainGrid}>
        <AuthenticationLeftGrid />

        <Grid item xs={12} sm={7} className={classes.rightGrid}>
          <Box className={classes.rightGridMainBox}>
            <Box>
              <Box
                className={classes.logoBox}
                component="img"
                src={logoIcon}
                alt="logoIcon"
              />
              <Typography className={classes.welcomeHeader} variant="h4">
                {OTP_SECTION.HEADING}
              </Typography>
              <Box>
                <Typography
                  className={classes.rightBoxInfoSubtitle}
                  color={lightPalette.carbonFiber.semiDark}
                  variant="subtitle1"
                >
                  {OTP_SECTION.SUBTITLE}
                </Typography>
                {otpError && (
                  <Typography
                    className={classes.rightBoxInfoSubtitleError}
                    color={lightPalette.colorRed.main}
                    variant="subtitle1"
                  >
                    {OTP_SECTION.ERROR}
                  </Typography>
                )}
              </Box>
              <Box className={classes.otpInputBoxes}>
                <Grid container id="otp-input" justifyContent="space-between">
                  {otp?.map((value, index) => (
                    <Grid key={`grid_${value.id}`}>
                      {!disableFields && (
                        <TextField
                          id={`otpInput${index + 1}`}
                          type="text"
                          key={`key${index + 1}`}
                          value={value}
                          className={classes.otpSingleBox}
                          autoFocus={index === 0}
                          inputProps={{ maxLength: 1, autoComplete: 'off' }}
                          disabled={disableFields && value === ''}
                          onChange={(e) => handleChange(index, e.target.value)}
                          onFocus={() => handleFocus(index)}
                          onClick={() => handleClick(index)}
                          onKeyDown={(e) => {
                            handleKeyDown(index, e);
                          }}
                          onPaste={(e) => handlePaste(e)}
                          style={{
                            pointerEvents:
                              index > 0 && otp[index - 1] === ''
                                ? 'none'
                                : 'auto',
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>

                {expiryTime !== 0 ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.otpSubmitButton}
                    onClick={handleSubmitOtp}
                  >
                    <Typography
                      variant="h5"
                      className={classes.otpSubmitTypography}
                    >
                      VERIFY
                    </Typography>
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.otpSubmitButton}
                    onClick={(e) => handleResendOtp(e)}
                  >
                    <Typography
                      variant="h5"
                      className={classes.otpSubmitTypography}
                    >
                      {renderResendButton(isLoading)}
                    </Typography>
                  </Button>
                )}

                {expiryTime !== 0 && (
                  <Typography
                    variant="body1"
                    className={classes.expiryTypography}
                  >
                    Expires in: {expiryTime} seconds
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Root>
  );
};

export default OtpSection;
